"use client";

//
// MakeUploadTab.tsx — Web counterpart of iOS `UploadView` in `MakePage.swift`.
//
// Mirrors the SwiftUI structure:
//   - header (publishTitle + subtitle)
//   - authBanner (DSCallout warning when not signed in)
//   - trackTitle (DSGlassCard with DSGlassTextField)
//   - songFileSection (DSGlassCard with DSListRow picker → <input type="file">)
//   - lyricsSection (DSGlassTabPill: autoDetect/none/file/text + dynamic body)
//   - albumArtSection (optional, DSListRow picker → <input type="file" accept="image/*">)
//   - bottomActionBar (sticky: publish button)
//

import { useRouter } from "@/infrastructure/router/navigation";
import { type ChangeEvent, useState } from "react";
import { useArtistSession } from "@/components/auth/ArtistSessionProvider";
import {
	ArtistIcon,
	Callout,
	GlassButton,
	GlassCard,
	GlassTabPill,
	GlassTextEditor,
	GlassTextField,
} from "@/components/design-system/ArtistDesignSystem";
import { LYRICS_MODE_LABELS, type LyricsInputMode } from "./makeCatalog";
import { measureAudioDuration } from "@/domain/media/LocalTrackAssetStore";
import { publishTrackToSupabase } from "@/infrastructure/supabase/trackUpload";
import { useTranslation } from "@/i18n/ArtistI18n";

const LYRICS_MAX_CHARS = 5000;

export interface MakeUploadTabProps {
	readonly isAuthenticated: boolean;
	readonly onAuthenticationRequired: () => void;
}
export function MakeUploadTab({
	isAuthenticated,
	onAuthenticationRequired,
}: Readonly<MakeUploadTabProps>) {
	const { t } = useTranslation();
	const router = useRouter();
	const session = useArtistSession();
	const [trackTitle, setTrackTitle] = useState("");
	const [songFile, setSongFile] = useState<File | null>(null);
	const [lyricsMode, setLyricsMode] = useState<LyricsInputMode>("autoDetect");
	const [lyricsFile, setLyricsFile] = useState<File | null>(null);
	const [lyricsText, setLyricsText] = useState("");
	const [albumFile, setAlbumFile] = useState<File | null>(null);
	const [continentFiles, setContinentFiles] = useState<readonly File[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);

	const isReadyToUpload =
		trackTitle.trim().length > 0 &&
		songFile !== null &&
		continentFiles.length >= 1 &&
		continentFiles.length <= 3;

	function handleSongFile(event: ChangeEvent<HTMLInputElement>) {
		const file = event.target.files?.[0];
		if (file !== undefined) {
			setSongFile(file);
			setSubmitError(null);
		}
	}

	function handleContinentFiles(event: ChangeEvent<HTMLInputElement>) {
		const files = Array.from(event.target.files ?? []).slice(0, 3);
		setContinentFiles(files);
		setSubmitError(null);
	}

	function handleLyricsFile(event: ChangeEvent<HTMLInputElement>) {
		const file = event.target.files?.[0];
		if (file !== undefined) {
			setLyricsFile(file);
			setSubmitError(null);
		}
	}

	function handleAlbumFile(event: ChangeEvent<HTMLInputElement>) {
		const file = event.target.files?.[0];
		if (file !== undefined) {
			setAlbumFile(file);
			setSubmitError(null);
		}
	}

	function handleLyricsTextChange(event: ChangeEvent<HTMLTextAreaElement>) {
		const next = event.target.value.slice(0, LYRICS_MAX_CHARS);
		setLyricsText(next);
	}

	async function handlePublish() {
		if (!isAuthenticated || session.phase !== "user" || session.scope === null) {
			onAuthenticationRequired();
			return;
		}
		if (!isReadyToUpload || isSubmitting || songFile === null) return;
		setIsSubmitting(true);
		setSubmitError(null);
		try {
			const importedLyrics =
				lyricsMode === "file" && lyricsFile !== null
					? (await lyricsFile.text()).slice(0, LYRICS_MAX_CHARS)
					: lyricsMode === "text"
						? lyricsText.slice(0, LYRICS_MAX_CHARS)
						: "";
			const durationSeconds = await measureAudioDuration(songFile);
			await publishTrackToSupabase({
				ownerUserId: session.scope.ownerUserId,
				title: trackTitle,
				durationSeconds,
				lyricsText: importedLyrics,
				...(lyricsFile === null ? {} : { lyricsFilename: lyricsFile.name }),
				song: songFile,
				cover: albumFile,
				continents: continentFiles,
			});
			router.push("/library");
		} catch {
			setSubmitError("作品未发布。请检查文件格式、网络或登录状态后重试。");
		} finally {
			setIsSubmitting(false);
		}
	}

	return (
		<div className="make-upload-tab">
			<header className="make-upload-tab__header">
				<h1>准备歌曲</h1>
				<p>选择音频、歌词、封面与 Continent，提交服务端审核并发布。</p>
			</header>

			{!isAuthenticated ? (
				<Callout
					style="warning"
					title="需要先登录"
					message="上传作品会绑定到你的账户，请先登录或注册。"
					actionLabel="去登录"
					onAction={onAuthenticationRequired}
				/>
			) : null}
			{submitError === null ? null : (
				<Callout style="error" title="无法准备歌曲" message={submitError} />
			)}

			<form
				className="make-upload-tab__form"
				aria-label="发布歌曲表单"
				onSubmit={(event) => {
					event.preventDefault();
					handlePublish();
				}}
			>
				<GlassCard className="make-upload-section">
					<header className="make-upload-section__header">
						<h2>歌曲标题</h2>
					</header>
					<GlassTextField
						label="歌曲标题"
						placeholder="给这首歌起个名字"
						value={trackTitle}
						onChange={(event) => setTrackTitle(event.target.value)}
						leading={<ArtistIcon name="doc" size={18} />}
						autoComplete="off"
						required
					/>
				</GlassCard>

				<GlassCard className="make-upload-section">
					<header className="make-upload-section__header">
						<h2>{t("make.continents")}</h2>
						<span className="make-upload-section__optional">必填 · 1–3 个</span>
					</header>
					<label className="make-upload-picker">
						<span className="make-upload-picker__icon" aria-hidden="true">
							<ArtistIcon name="image" size={22} />
						</span>
						<span className="make-upload-picker__copy">
							<strong>
								{continentFiles.length === 0
									? "选择图片或短视频"
									: `已选择 ${continentFiles.length} 个 Continent`}
							</strong>
							<small>图片最大 10 MB；视频最大 50 MB；最多 3 个</small>
						</span>
						<span className="make-upload-picker__trailing" aria-hidden="true">
							{continentFiles.length > 0 ? (
								<ArtistIcon name="check" size={20} />
							) : (
								<ArtistIcon name="chevron" size={18} style={{ transform: "rotate(90deg)" }} />
							)}
						</span>
						<input
							type="file"
							accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime"
							multiple
							className="make-upload-picker__input"
							onChange={handleContinentFiles}
						/>
					</label>
				</GlassCard>

				<GlassCard className="make-upload-section">
					<header className="make-upload-section__header">
						<h2>音频文件</h2>
					</header>
					<label className="make-upload-picker">
						<span className="make-upload-picker__icon" aria-hidden="true">
							<ArtistIcon name="music" size={22} />
						</span>
						<span className="make-upload-picker__copy">
							<strong>{songFile?.name ?? "选择音频文件"}</strong>
							<small>
								{songFile !== null
									? "已选择 · 点击替换"
									: "支持 MP3 / AAC / M4A，最大 50 MB"}
							</small>
						</span>
						<span className="make-upload-picker__trailing" aria-hidden="true">
							{songFile !== null ? (
								<ArtistIcon name="check" size={20} />
							) : (
								<ArtistIcon
									name="chevron"
									size={18}
									style={{ transform: "rotate(90deg)" }}
								/>
							)}
						</span>
						<input
							type="file"
							accept="audio/mpeg,audio/aac,audio/mp4,.mp3,.aac,.m4a"
							className="make-upload-picker__input"
							onChange={handleSongFile}
						/>
					</label>
				</GlassCard>

				<GlassCard className="make-upload-section">
					<header className="make-upload-section__header">
						<h2>歌词</h2>
						<span className="make-upload-section__optional">可选</span>
					</header>
					<GlassTabPill
						tabs={["autoDetect", "none", "file", "text"] as const}
						selected={lyricsMode}
						onSelect={(value) => setLyricsMode(value as LyricsInputMode)}
						label={(value) => LYRICS_MODE_LABELS[value as LyricsInputMode]}
					/>
					<div className="make-upload-section__lyrics-body">
						{lyricsMode === "autoDetect" ? (
							<Callout
								style="info"
								title="自动检测"
								message="上传后系统会自动从音频中识别歌词，若失败将留空。"
							/>
						) : null}
						{lyricsMode === "none" ? (
							<Callout
								style="info"
								title="纯音乐"
								message="标记为纯音乐，不附歌词文本。"
							/>
						) : null}
						{lyricsMode === "file" ? (
							<div className="make-upload-section__lyrics-file">
								<label className="make-upload-picker">
									<span className="make-upload-picker__icon" aria-hidden="true">
										<ArtistIcon name="doc" size={22} />
									</span>
									<span className="make-upload-picker__copy">
										<strong>{lyricsFile?.name ?? "选择歌词文件"}</strong>
										<small>
											{lyricsFile !== null
												? "已选择 · 点击替换"
												: "支持 .lrc / .txt"}
										</small>
									</span>
									<span
										className="make-upload-picker__trailing"
										aria-hidden="true"
									>
										{lyricsFile !== null ? (
											<ArtistIcon name="check" size={20} />
										) : (
											<ArtistIcon
												name="chevron"
												size={18}
												style={{ transform: "rotate(90deg)" }}
											/>
										)}
									</span>
									<input
										type="file"
										accept=".lrc,.txt,text/plain"
										className="make-upload-picker__input"
										onChange={handleLyricsFile}
									/>
								</label>
								{lyricsFile !== null ? (
									<button
										type="button"
										className="make-upload-section__lyrics-remove"
										onClick={() => setLyricsFile(null)}
									>
										移除文件
									</button>
								) : null}
							</div>
						) : null}
						{lyricsMode === "text" ? (
							<div className="make-upload-section__lyrics-text">
								<GlassTextEditor
									label="歌词文本"
									placeholder="粘贴或输入歌词..."
									value={lyricsText}
									onChange={handleLyricsTextChange}
									maxLength={LYRICS_MAX_CHARS}
								/>
								<div className="make-upload-section__lyrics-counter">
									<span>支持 LRC 时间标签；纯文本将自动对齐。</span>
									<span>
										{lyricsText.length}/{LYRICS_MAX_CHARS}
									</span>
								</div>
							</div>
						) : null}
					</div>
				</GlassCard>

				<GlassCard className="make-upload-section">
					<header className="make-upload-section__header">
						<h2>封面图片</h2>
						<span className="make-upload-section__optional">可选</span>
					</header>
					<label className="make-upload-picker">
						<span className="make-upload-picker__icon" aria-hidden="true">
							<ArtistIcon name="image" size={22} />
						</span>
						<span className="make-upload-picker__copy">
							<strong>{albumFile?.name ?? "选择封面图片"}</strong>
							<small>
								{albumFile !== null
									? "已选择 · 点击替换"
									: "正方形 1:1，JPG / PNG，最大 10 MB"}
							</small>
						</span>
						<span className="make-upload-picker__trailing" aria-hidden="true">
							{albumFile !== null ? (
								<ArtistIcon name="check" size={20} />
							) : (
								<ArtistIcon
									name="chevron"
									size={18}
									style={{ transform: "rotate(90deg)" }}
								/>
							)}
						</span>
						<input
							type="file"
							accept="image/*"
							className="make-upload-picker__input"
							onChange={handleAlbumFile}
						/>
					</label>
				</GlassCard>

				<section
					className="make-upload-tab__bottom-bar"
					aria-label="发布操作"
				>
					<GlassButton
						kind="primary"
						size="lg"
						expands
						isLoading={isSubmitting}
						isEnabled={isReadyToUpload && !isSubmitting}
						symbol={<ArtistIcon name="share" size={16} />}
						onClick={handlePublish}
					>
						{isAuthenticated ? "提交审核并发布" : "登录后发布"}
					</GlassButton>
				</section>

				<button
					type="submit"
					aria-hidden="true"
					tabIndex={-1}
					style={visuallyHidden}
				/>
			</form>
		</div>
	);
}

const visuallyHidden = {
	position: "absolute",
	width: "1px",
	height: "1px",
	padding: 0,
	margin: "-1px",
	overflow: "hidden",
	clip: "rect(0,0,0,0)",
	whiteSpace: "nowrap",
	border: 0,
} as const;
